import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { QuoteToolLiteStore } from '../store';
import { RouteIds } from '../types';
import { GoogleAnalyticsParametersType } from './google-analytics-parameters.type';
import { QuoteDataLayerService } from './quote-data-layer.service';
import { QuoteDataLayerStrategy, QuoteFieldsBuilders } from './quote-data-layer.strategy';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class AyaQuoteDataLayerStrategy implements QuoteDataLayerStrategy {
  readonly flow: Flow = Flows.Aya;
  quoteFieldsBuilders: QuoteFieldsBuilders | undefined;

  private readonly quoteDataLayerService = inject(QuoteDataLayerService);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);

  initializeDataLayerStepsBuilders(): void {
    this.quoteFieldsBuilders = {
      [RouteIds.course]: this.getQuoteBaseDataBuilder(),
      [RouteIds.extras]: this.getQuoteBaseDataBuilder(),
      [RouteIds.applicant]: this.getQuoteBaseDataBuilder(),
      [RouteIds.quote]: this.getQuoteBaseDataBuilder(),
      [RouteIds.booking]: this.getQuoteBaseDataBuilder(),
      [RouteIds.confirmation]: this.getQuoteBaseDataBuilder(),
      [RouteIds.payment]: this.getQuoteBaseDataBuilder(),
    };
  }

  buildDataLayer(): Observable<Partial<GoogleAnalyticsParametersType>> {
    return this.quoteDataLayerService.buildQuoteDataLayerObject(this.quoteFieldsBuilders);
  }

  /**
   * Returns a function with the selectors that retrieves base data fields such as language, school, accommodation type, program of interest...
   * and build the data layer for the initial steps in the quote process.
   */
  private getQuoteBaseDataBuilder() {
    return () => this.getQuoteBaseData();
  }

  private getQuoteBaseData() {
    return combineLatest([this.quoteDataLayerService.getBaseData(), this.quoteToolLiteStore.selectionForAyaFlowDataLayer$]).pipe(
      map(([baseData, { selectedSchoolLocation, selectedAccommodation, selectedAyaDuration }]) => ({
        ...baseData,
        quote_duration_interest: selectedAyaDuration?.weeks,
        quote_school_interest: selectedSchoolLocation?.schoolName,
        quote_accommodation_type: selectedAccommodation?.type,
      })),
    );
  }
}
